import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { LogoWithText } from "../components/Logo";
import { Toaster, toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Banknote,
  Loader2,
  Clock,
  CalendarDays,
  Phone,
  Mail,
  MapPin,
  Filter,
  Search,
  ChevronDown,
  ChevronUp,
  Leaf,
  Scissors,
  Car,
  Repeat,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  pending: { label: "Väntar", color: "bg-amber-100 text-amber-800 ring-amber-300" },
  approved: { label: "Godkänd", color: "bg-blue-100 text-blue-800 ring-blue-300" },
  paid: { label: "Betald", color: "bg-green-100 text-green-800 ring-green-300" },
  rejected: { label: "Nekad", color: "bg-red-100 text-red-800 ring-red-300" },
  cancelled: { label: "Avbokad", color: "bg-slate-100 text-slate-700 ring-slate-300" },
};

const SLOT_LABELS = {
  morgon: "Morgon · 08–12",
  eftermiddag: "Eftermiddag · 12–16",
  kvall: "Kväll · 16–20",
};

const SERVICE_META = {
  grasklippning: { label: "Gräsklippning", icon: Leaf },
  hacktrimning: { label: "Häcktrimning", icon: Scissors },
  biltvatt: { label: "Biltvätt", icon: Car },
};

const HEDGE_LABELS = {
  mini: "Mini (<2 lm)",
  liten: "Liten (2–5 lm)",
  mellan: "Mellan (5–15 lm)",
  stor: "Stor (15–25 lm)",
  jatte: "Jätte (>25 lm)",
};

const DAY_NAMES = ["sön", "mån", "tis", "ons", "tor", "fre", "lör"];

function parseISODate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function formatDate(iso) {
  const d = parseISODate(iso);
  if (!d) return "—";
  return `${DAY_NAMES[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}

function visitDates(start, isSub) {
  const d = parseISODate(start);
  if (!d) return [];
  const n = isSub ? 4 : 1;
  return Array.from({ length: n }, (_, i) => {
    const dd = new Date(d);
    dd.setDate(d.getDate() + i * 7);
    return dd;
  });
}

export default function AdminPage() {
  const { logout, authHeader, user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/bookings`, { headers: authHeader });
      setBookings(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sessionen har gått ut.");
        logout();
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Kunde inte hämta bokningar.");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    try {
      const res = await axios.patch(
        `${API}/admin/bookings/${id}/status`,
        { status },
        { headers: authHeader },
      );
      setBookings((prev) => prev.map((b) => (b.id === id ? res.data : b)));
      toast.success(`Status uppdaterad: ${STATUS_META[status]?.label || status}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Kunde inte uppdatera status.";
      toast.error(typeof msg === "string" ? msg : "Fel inträffade.");
    } finally {
      setUpdatingId(null);
    }
  };

  // Stats
  const stats = useMemo(() => {
    const acc = { total: bookings.length, pending: 0, approved: 0, paid: 0, revenue: 0 };
    for (const b of bookings) {
      const s = b.status || "pending";
      if (s in acc) acc[s] += 1;
      if (s === "paid") acc.revenue += b.estimated_price || 0;
    }
    return acc;
  }, [bookings]);

  // Filtering + search
  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    return bookings.filter((b) => {
      if (statusFilter !== "all" && (b.status || "pending") !== statusFilter) return false;
      if (!s) return true;
      return (
        (b.name || "").toLowerCase().includes(s) ||
        (b.email || "").toLowerCase().includes(s) ||
        (b.phone || "").toLowerCase().includes(s) ||
        (b.address || "").toLowerCase().includes(s) ||
        (b.id || "").toLowerCase().includes(s)
      );
    });
  }, [bookings, statusFilter, search]);

  // Group by date for week view (next 14 days)
  const weekView = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Array.from({ length: 14 }, (_, i) => {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      return { date: d, slots: [] };
    });
    for (const b of bookings) {
      if (b.status === "cancelled" || b.status === "rejected") continue;
      const dates = visitDates(b.booking_date, b.subscription);
      for (const visit of dates) {
        visit.setHours(0, 0, 0, 0);
        const day = days.find((x) => x.date.getTime() === visit.getTime());
        if (day) day.slots.push({ booking: b, visit });
      }
    }
    return days;
  }, [bookings]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <Toaster position="top-center" richColors />

      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="admin-logo-link">
            <LogoWithText />
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-sm text-slate-600">
              Inloggad som <strong className="text-slate-900">{user?.username || "admin"}</strong>
            </div>
            <button
              onClick={fetchBookings}
              data-testid="refresh-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-slate-300 hover:bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-700"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Uppdatera
            </button>
            <button
              onClick={() => {
                logout();
                navigate("/admin/login", { replace: true });
              }}
              data-testid="logout-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 text-sm font-semibold"
            >
              <LogOut size={14} /> Logga ut
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Stats */}
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Totalt" value={stats.total} testid="stat-total" />
          <StatCard label="Väntar" value={stats.pending} accent="amber" testid="stat-pending" />
          <StatCard label="Godkända" value={stats.approved} accent="blue" testid="stat-approved" />
          <StatCard label="Betalda (kr)" value={stats.revenue.toLocaleString("sv-SE")} accent="green" testid="stat-revenue" suffix=" kr" />
        </section>

        {/* Week view */}
        <section
          className="mt-10 rounded-2xl bg-white border border-slate-200 shadow-sm p-5 sm:p-6"
          data-testid="week-view"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900">
                Kommande 14 dagar
              </h2>
              <p className="text-xs text-slate-500">
                Visar inte avbokade eller nekade bokningar.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
            {weekView.map(({ date, slots }, i) => {
              const isToday = i === 0;
              const sun = date.getDay() === 0;
              return (
                <div
                  key={i}
                  className={`rounded-xl border p-2.5 min-h-[80px] ${
                    sun
                      ? "border-slate-200 bg-slate-50/60"
                      : isToday
                        ? "border-green-300 bg-green-50/60"
                        : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    {DAY_NAMES[date.getDay()]} {date.getDate()}/{date.getMonth() + 1}
                  </div>
                  {slots.length === 0 ? (
                    <div className="mt-2 text-[10px] text-slate-400">
                      {sun ? "Söndag" : "—"}
                    </div>
                  ) : (
                    <ul className="mt-1.5 space-y-1">
                      {slots.map(({ booking }, idx) => (
                        <li
                          key={`${booking.id}-${idx}`}
                          className="text-[10px] rounded-md bg-green-600 text-white px-1.5 py-0.5 truncate"
                          title={`${booking.name} (${SLOT_LABELS[booking.time_slot]})`}
                        >
                          {SLOT_LABELS[booking.time_slot]?.split(" ")[0]} · {booking.name.split(" ")[0]}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Filters */}
        <section className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={16} className="text-slate-500" />
            {[
              { id: "all", label: "Alla" },
              { id: "pending", label: "Väntar" },
              { id: "approved", label: "Godkända" },
              { id: "paid", label: "Betalda" },
              { id: "rejected", label: "Nekade" },
              { id: "cancelled", label: "Avbokade" },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setStatusFilter(f.id)}
                data-testid={`filter-${f.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  statusFilter === f.id
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              placeholder="Sök namn, e-post, telefon..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
              className="w-full sm:w-72 rounded-full border-0 py-2 pl-9 pr-4 text-sm text-slate-900 bg-white ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-green-600"
            />
          </div>
        </section>

        {/* Bookings list */}
        <section className="mt-6">
          {loading ? (
            <div className="py-20 flex items-center justify-center">
              <Loader2 size={28} className="animate-spin text-green-600" />
            </div>
          ) : visible.length === 0 ? (
            <div className="py-16 text-center text-slate-500 text-sm bg-white rounded-2xl border border-slate-200">
              Inga bokningar matchar filtret.
            </div>
          ) : (
            <ul className="space-y-3" data-testid="bookings-list">
              {visible.map((b) => (
                <BookingRow
                  key={b.id}
                  booking={b}
                  expanded={expanded === b.id}
                  onToggle={() => setExpanded((cur) => (cur === b.id ? null : b.id))}
                  onUpdateStatus={updateStatus}
                  busy={updatingId === b.id}
                />
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

function StatCard({ label, value, accent = "slate", suffix = "", testid }) {
  const colors = {
    slate: "from-slate-50 to-white border-slate-200",
    amber: "from-amber-50 to-white border-amber-200",
    blue: "from-blue-50 to-white border-blue-200",
    green: "from-green-50 to-white border-green-200",
  };
  return (
    <div
      className={`rounded-2xl border bg-gradient-to-br ${colors[accent]} p-5`}
      data-testid={testid}
    >
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div className="mt-2 text-3xl font-extrabold text-slate-900">
        {value}
        {suffix}
      </div>
    </div>
  );
}

function BookingRow({ booking, expanded, onToggle, onUpdateStatus, busy }) {
  const status = booking.status || "pending";
  const meta = STATUS_META[status] || STATUS_META.pending;
  const dates = visitDates(booking.booking_date, booking.subscription);

  return (
    <li
      className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
      data-testid={`booking-row-${booking.id}`}
    >
      <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${meta.color}`}
              data-testid={`status-badge-${booking.id}`}
            >
              {meta.label}
            </span>
            {booking.subscription && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-600 text-white px-2 py-0.5 text-[10px] font-bold">
                <Repeat size={10} /> ABO
              </span>
            )}
            <div className="font-bold text-slate-900 truncate">
              {booking.name}
            </div>
          </div>
          <div className="mt-1 text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <CalendarDays size={12} />
              {formatDate(booking.booking_date)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock size={12} /> {SLOT_LABELS[booking.time_slot]}
            </span>
            <span className="inline-flex items-center gap-1">
              <Phone size={12} /> {booking.phone}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 sm:gap-4">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {booking.subscription ? "Totalt (4 besök)" : "Total"}
            </div>
            <div className="text-lg font-extrabold text-slate-900">
              {booking.estimated_price} kr
            </div>
          </div>
          <button
            onClick={onToggle}
            data-testid={`expand-${booking.id}`}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-600"
            aria-label={expanded ? "Dölj" : "Visa"}
          >
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-200 p-4 sm:p-5 bg-slate-50/60">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <SectionTitle>Kontakt</SectionTitle>
              <ul className="space-y-1.5 text-sm text-slate-800">
                <li className="flex items-start gap-2">
                  <Mail size={14} className="mt-0.5 text-slate-400" />
                  <a href={`mailto:${booking.email}`} className="hover:text-green-700 break-all">
                    {booking.email}
                  </a>
                </li>
                <li className="flex items-start gap-2">
                  <Phone size={14} className="mt-0.5 text-slate-400" />
                  <a href={`tel:${booking.phone}`} className="hover:text-green-700">
                    {booking.phone}
                  </a>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin size={14} className="mt-0.5 text-slate-400" />
                  <span>{booking.address}</span>
                </li>
              </ul>
            </div>
            <div>
              <SectionTitle>Tjänster</SectionTitle>
              <ul className="space-y-1.5 text-sm text-slate-800">
                {booking.services.map((s) => {
                  const meta = SERVICE_META[s];
                  if (!meta) return null;
                  const Icon = meta.icon;
                  let detail = "";
                  if (s === "grasklippning" && booking.lawn_sqm) detail = ` — ${booking.lawn_sqm} kvm`;
                  else if (s === "hacktrimning" && booking.hedge_size) detail = ` — ${HEDGE_LABELS[booking.hedge_size]}`;
                  return (
                    <li key={s} className="flex items-start gap-2">
                      <Icon size={14} className="mt-0.5 text-green-600" />
                      <span>
                        {meta.label}
                        {detail}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-3 text-xs text-slate-500">
                <strong>Pris per besök:</strong> {booking.visit_price} kr
                {booking.subscription && " · 4 besök, −15% rabatt"}
              </div>
            </div>
          </div>

          {dates.length > 1 && (
            <div className="mt-4">
              <SectionTitle>Planerade besök</SectionTitle>
              <ul className="grid gap-1.5 sm:grid-cols-2 text-sm text-slate-800">
                {dates.map((d, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold">
                      {i + 1}
                    </span>
                    {DAY_NAMES[d.getDay()]} {d.getDate()}/{d.getMonth() + 1}/{d.getFullYear()} · {SLOT_LABELS[booking.time_slot]}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {booking.notes && (
            <div className="mt-4">
              <SectionTitle>Meddelande från kund</SectionTitle>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">
                {booking.notes}
              </p>
            </div>
          )}

          <div className="mt-5 text-[10px] text-slate-400">
            ID: {booking.id} · Skapad{" "}
            {new Date(booking.created_at).toLocaleString("sv-SE")}
          </div>

          {/* Actions */}
          <div className="mt-5 pt-5 border-t border-slate-200 flex flex-wrap gap-2">
            <ActionButton
              onClick={() => onUpdateStatus(booking.id, "approved")}
              disabled={busy || status === "approved" || status === "paid"}
              variant="primary"
              testid={`approve-${booking.id}`}
              icon={CheckCircle2}
              label="Godkänn"
            />
            <ActionButton
              onClick={() => onUpdateStatus(booking.id, "paid")}
              disabled={busy || status === "paid"}
              variant="success"
              testid={`paid-${booking.id}`}
              icon={Banknote}
              label="Markera som betald"
            />
            <ActionButton
              onClick={() => onUpdateStatus(booking.id, "rejected")}
              disabled={busy || status === "rejected"}
              variant="danger"
              testid={`reject-${booking.id}`}
              icon={XCircle}
              label="Neka"
            />
            <ActionButton
              onClick={() => onUpdateStatus(booking.id, "cancelled")}
              disabled={busy || status === "cancelled"}
              variant="ghost"
              testid={`cancel-${booking.id}`}
              icon={XCircle}
              label="Avboka"
            />
            <ActionButton
              onClick={() => onUpdateStatus(booking.id, "pending")}
              disabled={busy || status === "pending"}
              variant="ghost"
              testid={`pending-${booking.id}`}
              icon={Clock}
              label="Återställ"
            />
          </div>
        </div>
      )}
    </li>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
      {children}
    </div>
  );
}

function ActionButton({ onClick, disabled, variant, icon: Icon, label, testid }) {
  const variants = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white",
    success: "bg-green-600 hover:bg-green-700 text-white",
    danger: "bg-red-600 hover:bg-red-700 text-white",
    ghost: "bg-white ring-1 ring-inset ring-slate-300 text-slate-700 hover:bg-slate-50",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${variants[variant]}`}
    >
      <Icon size={13} /> {label}
    </button>
  );
}
